import React from 'react';
import { Dialog, DialogContent, Typography, Box, Button } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { motion, AnimatePresence } from 'framer-motion';

const ConfirmModal = ({ open, handleClose, handleConfirm, title, message, confirmText = "Delete", cancelText = "Cancel", severity = "error" }) => {
  const primaryColor = severity === "error" ? "#ef4444" : "#1E3A8A";
  const hoverColor = severity === "error" ? "#dc2626" : "#1E40AF";

  return (
    <AnimatePresence>
      {open && (
        <Dialog
          open={open}
          onClose={handleClose}
          PaperComponent={motion.div}
          PaperProps={{
            initial: { scale: 0.8, opacity: 0 },
            animate: { scale: 1, opacity: 1 },
            exit: { scale: 0.8, opacity: 0 },
            transition: { duration: 0.3 },
            sx: {
              borderRadius: '24px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
              padding: { xs: '24px', md: '40px' },
              textAlign: 'center',
              maxWidth: '450px',
              width: '90%',
              bgcolor: '#ffffff',
              overflow: 'hidden'
            },
          }}
          BackdropProps={{
            sx: {
              backgroundColor: 'rgba(0,0,0,0.1)',
              backdropFilter: 'blur(2px)',
            },
          }}
        >
          <DialogContent sx={{ p: 0 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Box 
                sx={{ 
                  width: 80, 
                  height: 80, 
                  borderRadius: '50%', 
                  bgcolor: severity === "error" ? '#fef2f2' : '#f0fdf4', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  mb: 3
                }}
              >
                <DeleteOutlineIcon 
                  sx={{ 
                    fontSize: 45, 
                    color: primaryColor
                  }} 
                />
              </Box>
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 'bold', 
                  mb: 2, 
                  color: '#1e293b',
                  fontSize: { xs: '1.4rem', md: '1.6rem' },
                  lineHeight: 1.2
                }}
              >
                {title || "Are you sure?"}
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: '#64748b', 
                  mb: 4,
                  fontSize: '1rem',
                  lineHeight: 1.5
                }}
              >
                {message}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, width: '100%', flexDirection: { xs: 'column', sm: 'row' } }}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleClose}
                  sx={{
                    borderRadius: '12px',
                    py: 1.5,
                    borderColor: '#e2e8f0',
                    color: '#64748b',
                    '&:hover': {
                      borderColor: '#cbd5e1',
                      bgcolor: '#f8fafc'
                    },
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 'bold',
                  }}
                >
                  {cancelText}
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleConfirm}
                  sx={{
                    borderRadius: '12px',
                    py: 1.5,
                    backgroundColor: primaryColor,
                    '&:hover': {
                      backgroundColor: hoverColor,
                    },
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    boxShadow: `0 4px 12px ${severity === "error" ? 'rgba(239, 68, 68, 0.25)' : 'rgba(30, 58, 138, 0.25)'}`
                  }}
                >
                  {confirmText}
                </Button>
              </Box>
            </Box>
          </DialogContent>
        </Dialog>
      )}
    </AnimatePresence>
  );
};

export default ConfirmModal;
